/**
 * Storage utility functions for localStorage management
 */

/**
 * Storage keys used by the application
 */
export const STORAGE_KEYS = {
  DASHBOARD_CONFIG: 'awesomeclocks:dashboard:config',
  WIDGETS: 'awesomeclocks:widgets',
  THEME: 'awesomeclocks:theme',
  WIDGET_PREFIX: 'awesomeclocks:widget:',
} as const;

/**
 * Type-safe localStorage operations
 */

/**
 * Get item from localStorage with type safety
 */
export function getStorageItem<T>(key: string, defaultValue?: T): T | null {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue ?? null;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Error reading from localStorage (key: ${key}):`, error);
    return defaultValue ?? null;
  }
}

/**
 * Set item in localStorage with type safety
 */
export function setStorageItem<T>(key: string, value: T): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Error writing to localStorage (key: ${key}):`, error);
    return false;
  }
}

/**
 * Remove item from localStorage
 */
export function removeStorageItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from localStorage (key: ${key}):`, error);
    return false;
  }
}

/**
 * Clear all items from localStorage
 */
export function clearStorage(): boolean {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
}

/**
 * Check if a key exists in localStorage
 */
export function hasStorageItem(key: string): boolean {
  return localStorage.getItem(key) !== null;
}

/**
 * Get all keys matching a prefix
 */
export function getStorageKeys(prefix?: string): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (!prefix || key.startsWith(prefix))) {
      keys.push(key);
    }
  }
  return keys;
}

/**
 * Remove all keys matching a prefix
 */
export function removeStorageKeysByPrefix(prefix: string): boolean {
  try {
    const keys = getStorageKeys(prefix);
    keys.forEach((key) => localStorage.removeItem(key));
    return true;
  } catch (error) {
    console.error(`Error removing keys with prefix (${prefix}):`, error);
    return false;
  }
}

/**
 * Get localStorage usage in bytes
 */
export function getStorageSize(): number {
  let total = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const value = localStorage.getItem(key);
      if (value) {
        total += key.length + value.length;
      }
    }
  }
  return total;
}

/**
 * Get localStorage usage in KB
 */
export function getStorageSizeKB(): number {
  return Math.round(getStorageSize() / 1024);
}

/**
 * Check if localStorage is available
 */
export function isStorageAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Storage event listener type
 */
export type StorageEventListener = (event: StorageEvent) => void;

/**
 * Add storage event listener (for cross-tab sync)
 */
export function addStorageListener(listener: StorageEventListener): void {
  window.addEventListener('storage', listener);
}

/**
 * Remove storage event listener
 */
export function removeStorageListener(listener: StorageEventListener): void {
  window.removeEventListener('storage', listener);
}

/**
 * Migrate storage data (useful for schema updates)
 */
export function migrateStorage<T, U>(key: string, migrateFn: (oldData: T) => U): boolean {
  try {
    const oldData = getStorageItem<T>(key);
    if (oldData === null) {
      return false;
    }
    const newData = migrateFn(oldData);
    return setStorageItem(key, newData);
  } catch (error) {
    console.error(`Error migrating storage (key: ${key}):`, error);
    return false;
  }
}

/**
 * Batch storage operations
 */
export function batchStorageOperations(
  operations: Array<{
    type: 'set' | 'remove';
    key: string;
    value?: unknown;
  }>
): boolean {
  try {
    operations.forEach((op) => {
      if (op.type === 'set' && op.value !== undefined) {
        setStorageItem(op.key, op.value);
      } else if (op.type === 'remove') {
        removeStorageItem(op.key);
      }
    });
    return true;
  } catch (error) {
    console.error('Error performing batch storage operations:', error);
    return false;
  }
}
